import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';

import { css } from '@flipster/styled-system/css';
import { center, hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import CautionCircleIcon from '../../icon/generated/CautionCircleIcon';
import CheckCircleIcon from '../../icon/generated/CheckCircleIcon';
import XMarkIcon from '../../icon/generated/XMarkIcon';
import { Portal } from '../../misc/portal';
import { IconButton } from '../icon-button';
import { SnackbarOptions, snackbarBunja } from './snackbar';

export const SnackbarLayout = () => {
  const { snackbarsAtom } = useBunja(snackbarBunja);
  const snackbars = useAtomValue(snackbarsAtom);

  return (
    <Portal selector='#toast'>
      <aside
        className={vstack({
          zIndex: 'toast',
          pos: 'fixed',
          left: '50%',
          bottom: { base: 64, tablet: 48 },
          gap: 12,
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
        })}
      >
        <AnimatePresence>
          {snackbars.map((s) => (
            <Snackbar key={s.id} {...s} />
          ))}
        </AnimatePresence>
      </aside>
    </Portal>
  );
};

const StatusIconMapper = {
  success: {
    svg: CheckCircleIcon,
    color: token('colors.content.accent.info'),
  },
  warning: {
    svg: CautionCircleIcon,
    color: token('colors.content.state.warning'),
  },
  error: {
    svg: CautionCircleIcon,
    color: token('colors.content.state.negative'),
  },
};

const Snackbar = ({ id, status, message, button }: SnackbarOptions) => {
  const { remove } = useBunja(snackbarBunja);

  const icon = StatusIconMapper[status];

  return (
    <m.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0, transition: { ease: 'easeOut', duration: 0.5 } }}
      exit={{ opacity: 0, y: 24, transition: { ease: 'easeIn', duration: 0.2 } }}
      layout
      className={hstack({
        textStyle: 'Text/M/Regular',
        minH: 48,
        minW: 'min(343px, calc(100dvw - 32px))',
        maxW: 'min(448px, calc(100dvw - 32px))',
        bgColor: 'background.accent.secondary',
        color: 'content.default.level-0',
        rounded: 4,
        pointerEvents: 'auto',
      })}
    >
      <div className={center({ ml: 12, mr: 8 })}>
        <icon.svg size={20} color={icon.color} />
      </div>
      <p className={css({ flex: 1, py: 14, whiteSpace: 'pre-wrap' })}>{message}</p>
      <div className={center({ ml: 12, mr: button ? 12 : 8 })} onClick={() => remove(id)}>
        {button ? (
          button
        ) : (
          <IconButton
            hierarchy='quaternary'
            size='medium'
            icon={<XMarkIcon color={token('colors.content.default.level-1')} />}
          />
        )}
      </div>
    </m.div>
  );
};
